import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { getCategories, getSubCategories } from "../api";
import "./HomeCategorySection.css";

export default function HomeCategorySection() {
  const [activeIndex, setActiveIndex] = useState(null);
  const [categoriesFromApi, setCategoriesFromApi] = useState([]);
  const [subcategoriesFromApi, setSubcategoriesFromApi] = useState([]);
  const navigate = useNavigate();

  // Hardcoded display data (images, background colours, display names)
  const hardcodedCategories = [
    {
      name: "Body Care",
      img: "/images/Category1.avif",
      bg: "#f1f5f9ff",
      fallbackSlug: "body-care"
    },
    {
      name: "Face Care",
      img: "/images/Category2.avif",
      bg: "#f9f5f1",
      fallbackSlug: "face-care"
    },
    {
      name: "Bath & Body",
      img: "/images/Category3.avif",
      bg: "#f1f5f9",
      fallbackSlug: "bath-body"
    },
    {
      name: "Hand Care",
      img: "/images/Category4.avif",
      bg: "#f9f1f5",
      fallbackSlug: "hand-care"
    },
  ];

  // Fetch real categories and subcategories from API
  useEffect(() => {
    const fetchData = async () => {
      try {
        // Fetch categories
        const catResponse = await getCategories();
        let cats = [];
        if (Array.isArray(catResponse)) cats = catResponse;
        else if (catResponse?.data && Array.isArray(catResponse.data)) cats = catResponse.data;
        else if (catResponse?.categories && Array.isArray(catResponse.categories)) cats = catResponse.categories;

        const formattedCats = cats.map(cat => ({
          id: cat.id || cat._id,
          name: (cat.name || cat.categoryName || cat.title || "").toLowerCase().trim(),
          slug: cat.slug || (cat.name || "").toLowerCase().replace(/\s+/g, '-').replace(/[^a-z0-9-]/g, '')
        }));
        setCategoriesFromApi(formattedCats);

        // Fetch subcategories
        const subResponse = await getSubCategories();
        let subs = [];
        if (Array.isArray(subResponse)) subs = subResponse;
        else if (subResponse?.data && Array.isArray(subResponse.data)) subs = subResponse.data;
        else if (subResponse?.subcategories && Array.isArray(subResponse.subcategories)) subs = subResponse.subcategories;

        const formattedSubs = subs.map(sub => ({
          id: sub.id || sub._id,
          name: sub.name || sub.subcategoryName || sub.title,
          slug: sub.slug || (sub.name || "").toLowerCase().replace(/\s+/g, '-').replace(/[^a-z0-9-]/g, ''),
          categoryId: sub.categoryId || sub.parentId || sub.parentCategoryId
        }));
        setSubcategoriesFromApi(formattedSubs);

      } catch (error) {
        console.error("Error fetching categories/subcategories:", error);
      }
    };
    fetchData();
  }, []);

  // Find a subcategory for a given category display name
  const getFirstSubcategoryForCategory = (displayName) => {
    const normalizedDisplay = displayName.toLowerCase().trim();
    const matchedCat = categoriesFromApi.find(cat => cat.name === normalizedDisplay);
    if (!matchedCat) return null;

    const sub = subcategoriesFromApi.find(sub => sub.categoryId === matchedCat.id);
    return sub ? sub.slug : null;
  };

  // Get category slug (used as fallback)
  const getCategorySlug = (displayName) => {
    const normalizedDisplay = displayName.toLowerCase().trim();
    const matched = categoriesFromApi.find(cat => cat.name === normalizedDisplay);
    if (matched) return matched.slug;
    // Fallback slug derived from display name
    return displayName
      .toLowerCase()
      .replace(/&/g, 'and')
      .replace(/\s+/g, '-')
      .replace(/[^a-z0-9-]/g, '');
  };

  const handleCategoryClick = (category) => {
    const catSlug = getCategorySlug(category.name);
    const subSlug = getFirstSubcategoryForCategory(category.name);

    // Build the URL
    let url = `/products?category=${catSlug}`;
    if (subSlug) url += `&subcategory=${subSlug}`;

    // Navigate and pass the category image as state
    navigate(url, {
      state: { bannerImage: category.img }   // <-- this ensures the banner uses the same image
    });
  };

  const handleRowClick = (category) => {
    handleCategoryClick(category);
  };

  const handleImageClick = (e, category) => {
    e.stopPropagation();
    handleCategoryClick(category);
  };

  return (
    <section
      className="category-section"
      style={{
        backgroundColor:
          activeIndex !== null ? hardcodedCategories[activeIndex].bg : "#ffffff",
      }}
    >
      <div className="category-inner">
        <p className="popular-text">Popular Collection</p>

        <div className="category-texts">
          {hardcodedCategories.map((cat, index) => (
            <div
              key={index}
              className={`category-row ${index % 2 === 0 ? "move-right" : "move-left"}`}
              tabIndex={0}
              role="button"
              onMouseEnter={() => setActiveIndex(index)}
              onMouseLeave={() => setActiveIndex(null)}
              onClick={() => handleRowClick(cat)}
              onKeyDown={(e) => {
                if (e.key === "Enter") handleRowClick(cat);
              }}
            >
              <h2 className="category-text">{cat.name}</h2>

              <div className="image-holder">
                {activeIndex === index && (
                  <div 
                    className="image-click-area" 
                    onClick={(e) => handleImageClick(e, cat)}
                    onMouseEnter={(e) => e.stopPropagation()}
                    onMouseLeave={(e) => e.stopPropagation()}
                  >
                    <img
                      src={cat.img}
                      alt={cat.name}
                      className="category-inline-image"
                    />
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}